package fr.frux.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.withFrameNanos
import androidx.navigation3.runtime.NavBackStack
import fr.frux.core.ads.AdHold
import fr.frux.core.ads.AdsController
import fr.frux.core.ads.HoldAds
import fr.frux.core.ads.InterstitialTrigger
import fr.frux.core.ads.LocalAdsController

/** Écrans où l'on ajoute, édite ou lance quelque chose : aucune publicité (ADR 0030 point 2, #528). */
internal fun Route?.isSensitiveFlowRoute(): Boolean =
    this is Route.AddTree || this is Route.CreateHarvest || this is Route.MoveTree || this is Route.CreateSite

/**
 * Pause naturelle qu'un changement de pile vient de créer, `null` si aucune. Seul un retour
 * (la pile raccourcit et l'écran du dessus change) en crée une : jamais un empilement, un
 * remplacement de pile (connexion) ni la restauration au démarrage.
 *
 * - fiche d'arbre quittée, tant qu'aucune ne l'a été dans ce process : [InterstitialTrigger.FIRST_TREE_DETAIL_CLOSED] ;
 * - arrivée sur la carte : [InterstitialTrigger.BACK_TO_MAP].
 *
 * Le flux sensible n'est pas jugé ici : le contrôleur le sait par ses holds (cf.
 * [AdsController.maybeShowInterstitial]).
 */
internal fun interstitialTriggerFor(
    previous: List<Route>,
    current: List<Route>,
    firstTreeDetailClosed: Boolean,
): InterstitialTrigger? {
    val before = previous.lastOrNull()
    val after = current.lastOrNull()
    val popped = current.size < previous.size && before != after
    return when {
        !popped -> null
        before is Route.TreeDetail && !firstTreeDetailClosed -> InterstitialTrigger.FIRST_TREE_DETAIL_CLOSED
        after is Route.Map -> InterstitialTrigger.BACK_TO_MAP
        else -> null
    }
}

/**
 * Publicité de la pile de navigation (#528) : pose le hold des flux sensibles et tente
 * l'interstitiel aux pauses naturelles. L'arbitrage reste dans [AdsController], ici on ne fait que
 * reconnaître les transitions.
 */
@Composable
internal fun InterstitialNavigationEffects(
    backStack: NavBackStack<Route>,
    controller: AdsController = LocalAdsController.current,
) {
    HoldAds(AdHold.SENSITIVE_FLOW, active = backStack.lastOrNull().isSensitiveFlowRoute(), controller = controller)

    var firstTreeDetailClosed by remember { mutableStateOf(false) }
    LaunchedEffect(backStack, controller) {
        var previous = backStack.toList()
        snapshotFlow { backStack.toList() }.collect { current ->
            val trigger = interstitialTriggerFor(previous, current, firstTreeDetailClosed)
            previous = current
            if (trigger != null) {
                if (trigger == InterstitialTrigger.FIRST_TREE_DETAIL_CLOSED) firstTreeDetailClosed = true
                // Le snapshot de la pile précède la composition qui libère le hold du flux sensible.
                // Sans cette image d'attente, le contrôleur voit le hold encore posé, puis la
                // libération tardive consomme à tort le déclencheur suivant.
                withFrameNanos { }
                controller.maybeShowInterstitial(trigger)
            }
        }
    }
}
